import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
    Database,
    Filter,
    GitMerge,
    Calculator,
    Sparkles,
    FileOutput,
    ArrowDownWideNarrow,
    Sigma,
    Layers,
    Cpu,
    Search,
    PanelLeftClose,
    PanelLeftOpen,
    TableProperties,
    FunctionSquare,
    LayoutGrid,
    LayoutList,
    ScanSearch,
    Split,
    ListOrdered,
    BrainCircuit,
    Zap,
    BarChart,
    ChevronDown,
    ChevronRight,
    SearchX,
    Brush,
    CopyMinus,
    Cloud,
    UploadCloud,
    Trash2,
    Type,
    Box,
    Mail,
    Send,
    Wand2,
    Calendar,
    MapPin,
    Coins,
    Server,
    UserCheck,
    FileSearch, // Added FileSearch
    BarChart3, // Added BarChart3
    Radio, // Added Radio
    Globe, // Added Globe
    Bot, Play, UserCog, CirclePause, FilePen, // Added Legacy Node Icons
    CalendarClock, Webhook, MessageSquare, FileText, Repeat,
    FolderSearch, GitBranch, ArrowRightLeft, Copy, Code, Merge, FileCode, ArrowUpDown
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { apiFetch, getStoredToken } from '@/api';

// Assuming apiFetch is a utility function for making API calls
// If not defined, you'd need to implement it or import it.


// Define a type for the category structure
interface CategoryItem {
    type: string;
    label: string;
    icon: React.ElementType;
    color: string;
    bg: string;
    border: string;
    description?: string;
    locked?: boolean;
    requiredFeature?: string;
}

interface Category {
    title: string;
    id: string;
    icon: React.ElementType;
    color: string;
    bg?: string;
    items: CategoryItem[];
}

// Map string icon names to Lucide React components
const ICON_MAP: Record<string, React.ElementType> = {
    Database, Filter, GitMerge, Calculator, Sparkles, FileOutput, ArrowDownWideNarrow,
    Sigma, Layers, Cpu, Search, PanelLeftClose, PanelLeftOpen, TableProperties,
    FunctionSquare, LayoutGrid, LayoutList, ScanSearch, Split, ListOrdered,
    BrainCircuit, Zap, BarChart, ChevronDown, ChevronRight, SearchX, Brush,
    CopyMinus, Cloud, UploadCloud, Trash2, Type, Box, Mail, Send, Wand2,
    Calendar, MapPin, Coins, Server, UserCheck, FileSearch, BarChart3, Radio,
    Globe, Bot, Play, UserCog, CirclePause, FilePen, CalendarClock, Webhook,
    MessageSquare, FileText, Repeat, ArrowUpDown,
    FolderSearch, GitBranch, ArrowRightLeft, Copy, Code, Merge, FileCode,
};


import { useAuth } from '@/context/useAuth';
import { STUDIOS, STUDIO_NODE_CATEGORIES } from '../../constants/studios.js';

export default function FlowSidebar({ onFileUploaded }: { onFileUploaded?: (result: any) => void }) {
    const [isOpen, setIsOpen] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [categories, setCategories] = useState<Category[]>([]);
    const [lockedCategories, setLockedCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState(true);
    const [collapsedCats, setCollapsedCats] = useState<Record<string, boolean>>({});
    const [showLocked, setShowLocked] = useState(false);

    // ── File Drop State ───────────────────────────────────────────────────────
    const [isDragOver, setIsDragOver] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);


    useEffect(() => {
        fetchSchema();
    }, []); // Fetch once — backend already filters by tenant features

    const fetchSchema = async () => {
        try {
            // Pass include_locked=true so we also get locked nodes for the discoverable section
            const schema = await apiFetch('/flows/schema?include_locked=true');
            if (schema) {
                const enabledCats: Record<string, Category> = {};
                const lockedCats: Record<string, Category> = {};

                // Theme Mapping for Functional Categories
                const THEME_MAP: Record<string, any> = {
                    "Sources": { color: 'text-blue-600', bg: 'bg-blue-100/50', border: 'hover:border-blue-300', icon: Database },
                    "Preparation": { color: 'text-teal-600', bg: 'bg-teal-100/50', border: 'hover:border-teal-300', icon: Brush },
                    "Transformation": { color: 'text-violet-600', bg: 'bg-violet-100/50', border: 'hover:border-violet-300', icon: GitMerge },
                    "Logic": { color: 'text-amber-600', bg: 'bg-amber-100/50', border: 'hover:border-amber-300', icon: Split },
                    "Output": { color: 'text-sky-600', bg: 'bg-sky-100/50', border: 'hover:border-sky-300', icon: FileOutput },
                    "AI/ML": { color: 'text-fuchsia-600', bg: 'bg-fuchsia-100/50', border: 'hover:border-fuchsia-300', icon: BrainCircuit },
                    "Insights": { color: 'text-pink-600', bg: 'bg-pink-100/50', border: 'hover:border-pink-300', icon: BarChart3 },
                    "Triggers": { color: 'text-orange-600', bg: 'bg-orange-100/50', border: 'hover:border-orange-300', icon: CalendarClock },
                    "Control Flow": { color: 'text-amber-600', bg: 'bg-amber-100/50', border: 'hover:border-amber-300', icon: Split },
                    "Utility": { color: 'text-slate-600', bg: 'bg-slate-100/50', border: 'hover:border-slate-300', icon: Box },
                    "Actions": { color: 'text-green-600', bg: 'bg-green-100/50', border: 'hover:border-green-300', icon: Play },
                    "Integrations": { color: 'text-orange-600', bg: 'bg-orange-100/50', border: 'hover:border-orange-300', icon: Globe },
                    "Others": { color: 'text-slate-500', bg: 'bg-slate-100/50', border: 'hover:border-slate-300', icon: Box }
                };

                // Node types to hide from the palette entirely
                const HIDDEN_NODE_TYPES = new Set([
                    // Teams
                    'teams', 'TEAMS', 'TEAMS_NOTIFY',
                    // MDM
                    'mdmIngest', 'MDM_INGEST',
                    // NOTE: signalStart is intentionally NOT hidden — it is the trigger node
                    // for the support ticket workflow (ticket_created signal).
                    // Set / Get variables
                    'setVariable', 'SET_VARIABLE', 'SETVARIABLE',
                    'getVariable', 'GET_VARIABLE', 'GETVARIABLE',
                    // Flow Runner
                    'flowRunner', 'FLOW_RUNNER', 'FLOWRUNNER',
                    // Date Utils
                    'dateUtils', 'DATE_UTILS', 'DATEUTILS', 'dateUtil', 'DATE_UTIL',
                    // Data Model (output)
                    'dataModel', 'DATA_MODEL', 'data_model',
                ]);

                Object.entries(schema).forEach(([nodeType, nodeDef]: [string, any]) => {
                    // Skip hidden nodes
                    if (HIDDEN_NODE_TYPES.has(nodeType)) return;

                    const meta = nodeDef.metadata || {};
                    const catName = meta.category || 'Others';
                    const isLocked = meta.locked === true;
                    const theme = THEME_MAP[catName] || THEME_MAP['Others'];

                    const bucket = isLocked ? lockedCats : enabledCats;

                    if (!bucket[catName]) {
                        bucket[catName] = {
                            title: catName,
                            id: catName.toLowerCase().replace(/\s+/g, '-'),
                            icon: theme.icon,
                            color: theme.color.replace('text-', ''),
                            bg: theme.bg,
                            items: []
                        };
                    }

                    const uiHints = meta.uiHints || {};
                    const IconComponent = ICON_MAP[uiHints.icon] || Box;

                    bucket[catName].items.push({
                        type: nodeType,
                        label: meta.title || nodeType,
                        icon: IconComponent,
                        color: theme.color,
                        bg: theme.bg,
                        border: theme.border,
                        description: meta.description,
                        locked: isLocked,
                        requiredFeature: meta.requiredFeature
                    });
                });

                // Enforce Functional Order
                const order = [
                    "Sources", "Preparation", "Transformation", "Insights",
                    "AI/ML", "Control Flow", "Actions", "Triggers", "Utility", "Output", "Others"
                ];

                const sortBucket = (cats: Record<string, Category>) => {
                    const sorted = order.map(k => cats[k]).filter(Boolean);
                    Object.keys(cats).forEach(k => { if (!order.includes(k)) sorted.push(cats[k]); });
                    return sorted;
                };

                const sortedEnabled = sortBucket(enabledCats);
                const sortedLocked = sortBucket(lockedCats);

                setCategories(sortedEnabled);
                setLockedCategories(sortedLocked);

                // Default collapse state — everything collapsed by default
                const defaultCollapsed: Record<string, boolean> = {};
                sortedEnabled.forEach((c) => {
                    defaultCollapsed[c.id] = true;
                });
                setCollapsedCats(defaultCollapsed);
            }
        } catch (error) {
            console.error("Failed to fetch node schema:", error);
        } finally {
            setLoading(false);
        }
    };

    const onDragStart = (event: React.DragEvent, nodeType: string, label: string) => {
        event.dataTransfer.setData('application/reactflow/type', nodeType);
        event.dataTransfer.setData('application/reactflow/label', label);
        event.dataTransfer.effectAllowed = 'move';
    };

    const filteredCategories = useMemo(() => {
        if (!searchTerm) return categories;
        return categories.map(cat => ({
            ...cat,
            items: cat.items.filter(item =>
                item.label.toLowerCase().includes(searchTerm.toLowerCase())
            )
        })).filter(cat => cat.items.length > 0);
    }, [searchTerm, categories]);

    const toggleCategory = (id: string) => {
        setCollapsedCats(prev => ({ ...prev, [id]: !prev[id] }));
    };

    // ── File Upload Handler ───────────────────────────────────────────────────
    const handleFileUpload = useCallback(async (file: File) => {
        const ext = (file.name.split('.').pop() || '').toLowerCase();
        const ALLOWED = new Set(['csv', 'xlsx', 'xls', 'json', 'parquet']);
        if (!ALLOWED.has(ext)) {
            setUploadError(`Unsupported file type: .${ext}`);
            return;
        }
        const token = getStoredToken();
        if (!token) { setUploadError('Not logged in.'); return; }

        setIsUploading(true);
        setUploadError(null);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const result = await apiFetch('/flows/ops/upload', token, { method: 'POST', body: formData });
            // Notify canvas via callback or custom event
            if (onFileUploaded) {
                onFileUploaded(result);
            } else {
                window.dispatchEvent(new CustomEvent('flow:file-uploaded', { detail: result }));
            }
        } catch (err: any) {
            setUploadError(err?.message || 'Upload failed');
        } finally {
            setIsUploading(false);
        }
    }, [onFileUploaded]);

    const onDropZoneDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file) handleFileUpload(file);
    }, [handleFileUpload]);

    return (
        <aside
            className={`
                bg-white border-r border-gray-200 flex flex-col transition-all duration-300 ease-in-out shadow-sm
                ${isOpen ? 'w-64' : 'w-14 items-center'}
            `}
        >
            {/* Header */}
            <div className={`
                h-12 flex items-center border-b border-gray-100 flex-shrink-0
                ${isOpen ? 'px-3 justify-between' : 'justify-center'}
            `}>
                {isOpen && (
                    <span className="font-bold text-xs text-gray-800 tracking-wide uppercase">Toolbox</span>
                )}
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-gray-400 hover:text-gray-900 hover:bg-gray-100"
                    onClick={() => setIsOpen(!isOpen)}
                >
                    {isOpen ? <PanelLeftClose className="h-4 w-4" /> : <PanelLeftOpen className="h-4 w-4" />}
                </Button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-hidden flex flex-col bg-gray-50/30">
                {isOpen ? (
                    <>
                        {/* Search */}
                        <div className="p-3 pb-2">
                            <div className="relative group">
                                <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                                <input
                                    type="text"
                                    placeholder="Find nodes..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full text-xs pl-8 pr-8 py-2 bg-white border border-gray-200 focus:border-blue-400 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500/10 focus:outline-none transition-all text-gray-700 placeholder:text-gray-400"
                                />
                                {searchTerm && (
                                    <button
                                        onClick={() => setSearchTerm('')}
                                        className="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600"
                                    >
                                        <SearchX className="h-3.5 w-3.5" />
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Node List */}
                        <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-4 scrollbar-thin scrollbar-thumb-gray-200 hover:scrollbar-thumb-gray-300">
                            {loading && <div className="text-center text-xs text-gray-400 py-4">Loading nodes...</div>}

                            {filteredCategories.length === 0 && !loading ? (
                                <div className="text-center py-8 text-gray-400">
                                    <p className="text-xs">No nodes found</p>
                                </div>
                            ) : (
                                filteredCategories.map((cat) => (
                                    <div key={cat.id} className="space-y-1.5">
                                        {/* Category Header */}
                                        <button
                                            onClick={() => toggleCategory(cat.id)}
                                            className="w-full flex items-center justify-between px-1 py-1 text-gray-500 hover:text-gray-800 transition-colors group"
                                        >
                                            <div className="flex items-center gap-2">
                                                {collapsedCats[cat.id] ? (
                                                    <ChevronRight className="h-3 w-3 text-gray-400 group-hover:text-gray-600" />
                                                ) : (
                                                    <ChevronDown className="h-3 w-3 text-gray-400 group-hover:text-gray-600" />
                                                )}
                                                <div className={`p-1 rounded-md ${cat.bg || 'bg-gray-100'}`}>
                                                    <cat.icon className={`h-3 w-3 ${cat.color ? `text-${cat.color}` : 'text-gray-500'}`} />
                                                </div>
                                                <span className="text-xs font-semibold text-gray-700">{cat.title}</span>
                                            </div>
                                            <span className="text-[10px] text-gray-400 font-medium bg-gray-100 px-1.5 py-0.5 rounded-full">
                                                {cat.items.length}
                                            </span>
                                        </button>

                                        {/* Grid */}
                                        <div
                                            className={`
                                                grid grid-cols-3 gap-2 pl-2 transition-all duration-300 ease-in-out origin-top
                                                ${collapsedCats[cat.id] ? 'hidden opacity-0 scale-y-95 h-0' : 'block opacity-100 scale-y-100'}
                                            `}
                                        >
                                            {cat.items.map((item) => (
                                                <div
                                                    key={item.type}
                                                    className={`group flex flex-col items-center justify-center p-1.5 rounded-lg border border-transparent bg-white shadow-[0_1px_4px_-1px_rgba(0,0,0,0.05)] hover:shadow-[0_2px_8px_-2px_rgba(0,0,0,0.1)] cursor-grab active:cursor-grabbing transition-all duration-200 relative overflow-hidden h-[64px] hover:-translate-y-0.5 ${item.border || ''}`}
                                                    onDragStart={(event) => onDragStart(event, item.type, item.label)}
                                                    draggable
                                                    title={item.description}
                                                >
                                                    {/* Icon Box (Compact) */}
                                                    <div className={`p-1.5 rounded-lg mb-1 ${item.bg} group-hover:scale-110 transition-transform duration-200`}>
                                                        {item.icon && <item.icon className={`h-4 w-4 ${item.color}`} />}
                                                    </div>
                                                    {/* Label */}
                                                    <span className="text-[10px] font-medium text-gray-600 text-center leading-tight line-clamp-2 w-full group-hover:text-gray-900">
                                                        {item.label}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))
                            )}

                            {/* ── Locked Nodes Section (Feature Upsell) ── */}
                            {lockedCategories.length > 0 && (
                                <div className="mt-4 border-t border-gray-100 pt-3">
                                    <button
                                        onClick={() => setShowLocked(!showLocked)}
                                        className="w-full flex items-center justify-between px-1 py-1 text-gray-400 hover:text-gray-600 transition-colors group"
                                    >
                                        <div className="flex items-center gap-2">
                                            {showLocked ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                            <span className="text-xs font-semibold">🔒 More Nodes Available</span>
                                        </div>
                                        <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">
                                            {lockedCategories.reduce((sum, c) => sum + c.items.length, 0)}
                                        </span>
                                    </button>

                                    {showLocked && (
                                        <div className="mt-2 space-y-3">
                                            {lockedCategories.map((cat) => (
                                                <div key={cat.id} className="space-y-1">
                                                    <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">{cat.title}</div>
                                                    <div className="grid grid-cols-3 gap-2 pl-2">
                                                        {cat.items.map((item) => (
                                                            <div
                                                                key={item.type}
                                                                className="group flex flex-col items-center justify-center p-1.5 rounded-lg border border-dashed border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed h-[64px] relative"
                                                                title={`Requires '${item.requiredFeature}' feature — contact your administrator to enable`}
                                                            >
                                                                <div className="p-1.5 rounded-lg mb-1 bg-gray-100 grayscale">
                                                                    {item.icon && <item.icon className="h-4 w-4 text-gray-400" />}
                                                                </div>
                                                                <span className="text-[10px] font-medium text-gray-400 text-center leading-tight line-clamp-2 w-full">
                                                                    {item.label}
                                                                </span>
                                                                {/* Lock badge */}
                                                                <div className="absolute top-1 right-1 text-[8px] text-amber-500">🔒</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                            <p className="text-[10px] text-center text-gray-400 py-1">
                                                These nodes require a plan upgrade.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ── File Drop Zone ── */}
                            <div className="mt-3 border-t border-gray-100 pt-3">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".csv,.xlsx,.xls,.json,.parquet"
                                    className="hidden"
                                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileUpload(f); e.target.value = ''; }}
                                />
                                <div
                                    className={`
                                        relative flex flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed
                                        cursor-pointer py-3 px-2 transition-all duration-200 select-none
                                        ${isDragOver
                                            ? 'border-blue-400 bg-blue-50 scale-[0.98]'
                                            : 'border-gray-200 bg-gray-50/80 hover:border-blue-300 hover:bg-blue-50/40'
                                        }
                                    `}
                                    onClick={() => fileInputRef.current?.click()}
                                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); }}
                                    onDragLeave={() => setIsDragOver(false)}
                                    onDrop={onDropZoneDrop}
                                >
                                    {isUploading ? (
                                        <>
                                            <div className="h-5 w-5 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
                                            <span className="text-[10px] text-blue-600 font-medium">Uploading…</span>
                                        </>
                                    ) : (
                                        <>
                                            <UploadCloud className={`h-5 w-5 ${isDragOver ? 'text-blue-500' : 'text-gray-400'}`} />
                                            <span className="text-[10px] font-semibold text-gray-600 text-center leading-snug">
                                                Drop CSV / Excel / JSON
                                            </span>
                                            <span className="text-[9px] text-gray-400">or click to browse</span>
                                        </>
                                    )}
                                    {uploadError && (
                                        <span className="text-[9px] text-red-500 text-center mt-0.5">{uploadError}</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="flex-1 flex flex-col items-center py-4 gap-3 overflow-y-auto scrollbar-none">
                        {categories.map((cat) => (
                            <div
                                key={cat.id}
                                className="group relative p-2 rounded-lg hover:bg-gray-100/80 text-gray-400 hover:text-blue-600 transition-all cursor-pointer"
                                onClick={() => setIsOpen(true)}
                            >
                                <cat.icon className="h-5 w-5" strokeWidth={1.5} />
                                {/* Tooltip */}
                                <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 px-2 py-1.5 bg-gray-800 text-white text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-all translate-x-1 group-hover:translate-x-0">
                                    {cat.title}
                                    <div className="absolute left-0 top-1/2 -translate-x-1 -translate-y-1/2 border-4 border-transparent border-r-gray-800"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </aside>
    );
}
